import helper from '../../utils/helper'
import tourManager from '../TourManager'
import demoStore from './mock/DemoStore'
import { enableDemoInterceptor, disableDemoInterceptor } from './mock/axiosDemoInterceptor'
import { mountDemoExitUi, unmountDemoExitUi, ensureDemoUiStyles, setDemoResumeVisible, showDemoExitConfirmation, isDemoExitConfirmActive, setDemoExitConfirmActive } from './DemoExitButton'
import { isDemoAllowedPath } from './DemoFlow'
import { canUseTours } from '../tourUi'
import { SAVE_UNLOCK_CLASS, lockDemoSaveButton, findDemoSaveButton } from './demoSaveControls'
export { unlockDemoSaveButton, lockDemoSaveButton, withNativeDemoSaveAllowed, SAVE_UNLOCK_CLASS } from './demoSaveControls'

const SESSION_KEY = 'hdv_demo_session'
const RESUME_STORAGE_KEY = 'hdv_tour_resume'
const PROGRESS_STORAGE_KEY = 'hdv_tour_progress'
const DEMO_PAUSE_KEY = 'hdv_demo_pause'
const STEP_CONTEXT_STORAGE_KEY = 'hdv_demo_step_contexts'
const STORE_KEY = 'hdv_demo_store'
const GUIDE_DONE_KEY = 'hdv_demo_guide_done'
const RESTART_KEY = 'hdv_demo_restart'

class DemoSession {
    constructor() {
        this.active = false
        this.bootstrapped = false
        this.routeUnlisten = null
        this.lastAllowedPath = '/'
        this.guardOpen = false
        this._origMinimize = null
        this._minimizeHooked = false
        this._restartConfirmOpen = false
        this._pendingRestart = false

        this._onGuideFinished = () => {
            if (!this.isActive()) return
            this.markGuideDone()
            this.reloadAfterGuide()
        }
        document.addEventListener('hdv-demo-guide-finished', this._onGuideFinished)
    }

    isActive() {
        if (this.active) return true
        try {
            return sessionStorage.getItem(SESSION_KEY) === '1'
        } catch (error) {
            return false
        }
    }

    bootstrap() {
        if (this.bootstrapped) return
        this.bootstrapped = true
        ensureDemoUiStyles()

        if (!canUseTours()) {
            if (this.isActive()) {
                this.stop({ redirectTo: false })
            }
            return
        }

        if (!this.isActive()) return

        this.active = true
        demoStore.load()
        enableDemoInterceptor()
        this.mountUi()
        this.attachRouteGuard()
        this.attachFieldInteractionGuard()
        this.attachSaveButtonGuard()
        this.attachHelpGuard()
        this.hookMinimize()

        try {
            if (sessionStorage.getItem(RESTART_KEY) === '1') {
                sessionStorage.removeItem(RESTART_KEY)
                this._pendingRestart = true
            }
        } catch (error) {}

        if (!this._pendingRestart && this.hasPausedTour() && !tourManager.isActive() && !this.isGuideDone()) {
            setDemoResumeVisible(true)
        }
    }

    mountUi() {
        mountDemoExitUi({
            onExit: () => this.stop(),
            onResume: () => this.resume(),
            onRequestExit: () => this.requestExit(),
        })
        if (!this._onDemoExitRequest) {
            this._onDemoExitRequest = () => this.requestExit()
            document.addEventListener(
                'hdv-demo-request-exit',
                this._onDemoExitRequest
            )
        }
        setDemoResumeVisible(this.hasPausedTour() && !tourManager.isActive() && !this.isGuideDone())
    }

    requestExit() {
        if (this._exitConfirmOpen || isDemoExitConfirmActive()) return
        this._exitConfirmOpen = true
        setDemoExitConfirmActive(true)

        if (tourManager.isActive()) {
            tourManager.minimize()
        }

        setDemoResumeVisible(false)

        showDemoExitConfirmation(
            () => {
                this._exitConfirmOpen = false
                setDemoExitConfirmActive(false)
                this.stop()
            },
            () => {
                this._exitConfirmOpen = false
                setDemoExitConfirmActive(false)
                if (this.isGuideDone()) {
                    setDemoResumeVisible(false)
                    return
                }
                this.resume()
            }
        )
    }

    isExitConfirmOpen() {
        return Boolean(this._exitConfirmOpen) || isDemoExitConfirmActive()
    }

    attachFieldInteractionGuard() {
        if (this._onDemoFieldInteraction) return

        this._onDemoFieldInteraction = (event) => {
            if (!this.isActive() || !event.isTrusted) return

            const target = event.target
            const isField = target?.matches?.(
                'input, select, textarea, [contenteditable="true"]'
            )
            const isDemoForm = target?.closest?.(
                '.hdv-form-default, [data-tour$="-form-root"]'
            )

            if (isField && isDemoForm) {
                event.preventDefault()
                event.stopPropagation()
                event.stopImmediatePropagation?.()
            }
        }

        ;['beforeinput', 'keydown', 'change', 'click'].forEach((eventName) => {
            document.addEventListener(
                eventName,
                this._onDemoFieldInteraction,
                true
            )
        })
    }

    detachFieldInteractionGuard() {
        if (!this._onDemoFieldInteraction) return

        ;['beforeinput', 'keydown', 'change', 'click'].forEach((eventName) => {
            document.removeEventListener(
                eventName,
                this._onDemoFieldInteraction,
                true
            )
        })
        this._onDemoFieldInteraction = null
    }

    attachSaveButtonGuard() {
        if (this._onDemoSaveClick) return

        this._onDemoSaveClick = (event) => {
            if (!this.isActive() || !event.isTrusted) return

            const button = findDemoSaveButton(event.target)
            if (!button) return

            if (button._hdvAllowNativeSave) return

            if (
                tourManager.isActive() &&
                button.classList.contains(SAVE_UNLOCK_CLASS) &&
                button._hdvDemoSaveHandler
            ) {
                return
            }

            event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation?.()
        }

        document.addEventListener('click', this._onDemoSaveClick, true)
    }

    detachSaveButtonGuard() {
        if (!this._onDemoSaveClick) return
        document.removeEventListener('click', this._onDemoSaveClick, true)
        this._onDemoSaveClick = null
        document
            .querySelectorAll(`.${SAVE_UNLOCK_CLASS}[data-tour$="-salvar"]`)
            .forEach((button) => lockDemoSaveButton(button))
    }

    attachHelpGuard() {
        if (this._onDemoHelpClick) return

        this._onDemoHelpClick = (event) => {
            if (!this.isActive()) return

            const help = event.target.closest?.(
                '.hdv-help-tour-icon, [data-tour^="help-"], .hdv-navbar-item[title="Ajuda"]'
            )
            if (!help) return

            event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation?.()
            this.handleHelpClick()
        }

        document.addEventListener('click', this._onDemoHelpClick, true)
    }

    detachHelpGuard() {
        if (!this._onDemoHelpClick) return
        document.removeEventListener('click', this._onDemoHelpClick, true)
        this._onDemoHelpClick = null
    }

    hookMinimize() {
        if (this._minimizeHooked) return
        this._minimizeHooked = true
        this._origMinimize = tourManager.minimize.bind(tourManager)
        this._origStart = tourManager.start.bind(tourManager)

        tourManager.minimize = () => {
            if (this.isActive()) {
                this.savePauseFromActiveTour()
            }
            this._origMinimize()
            if (this.isActive()) {
                document
                    .querySelectorAll('[data-tour$="-salvar"]')
                    .forEach((button) => lockDemoSaveButton(button))
                if (!this.isExitConfirmOpen() && !this.isGuideDone()) {
                    setDemoResumeVisible(true)
                }
            }
        }

        tourManager.start = async (...args) => {
            const [name, options = {}] = args
            if (this.isActive()) {
                setDemoResumeVisible(false)
                if (
                    this.isGuideDone() &&
                    !options.fromDemoRestart &&
                    (name === 'introdutorio' || String(name).startsWith('demo-'))
                ) {
                    this.requestRestart()
                    return
                }
            }
            return this._origStart(...args)
        }
    }

    savePauseFromActiveTour() {
        const tourName = tourManager.activeTourName
        const resumeIndex = tourManager.getActiveIndex?.() ?? 0

        if (!tourName) return

        const activeTabs = Array.from(
            document.querySelectorAll('.react-tabs__tab--selected[data-tour]')
        )
            .map((tab) => tab.getAttribute('data-tour'))
            .filter(Boolean)

        try {
            sessionStorage.setItem(
                DEMO_PAUSE_KEY,
                JSON.stringify({
                    tourName,
                    resumeIndex: Number.isInteger(resumeIndex) ? resumeIndex : 0,
                    path:
                        window.location.pathname +
                        window.location.search +
                        window.location.hash,
                    activeTabs,
                })
            )
        } catch (error) {}
    }

    hasPausedTour() {
        try {
            const raw = sessionStorage.getItem(DEMO_PAUSE_KEY)
            if (!raw) return false
            const pause = JSON.parse(raw)
            return Boolean(pause?.tourName)
        } catch (error) {
            return false
        }
    }

    getPausedTour() {
        try {
            const raw = sessionStorage.getItem(DEMO_PAUSE_KEY)
            return raw ? JSON.parse(raw) : null
        } catch (error) {
            return null
        }
    }

    clearPause() {
        try {
            sessionStorage.removeItem(DEMO_PAUSE_KEY)
        } catch (error) {}
        setDemoResumeVisible(false)
    }

    isGuideDone() {
        try {
            return sessionStorage.getItem(GUIDE_DONE_KEY) === '1'
        } catch (error) {
            return false
        }
    }

    markGuideDone() {
        try {
            sessionStorage.setItem(GUIDE_DONE_KEY, '1')
        } catch (error) {}
        this.clearPause()
    }

    reloadAfterGuide() {
        const { pathname, hash } = window.location
        window.location.replace(`${pathname}?_=${Date.now()}${hash || ''}`)
    }

    hasResumableDemoTour() {
        if (this.hasPausedTour()) return true

        try {
            const rawProgress = sessionStorage.getItem(PROGRESS_STORAGE_KEY)
            const progress = rawProgress ? JSON.parse(rawProgress) : null
            return Boolean(progress?.tourName?.startsWith?.('demo-'))
        } catch (error) {
            return false
        }
    }

    handleHelpClick() {
        if (!canUseTours() || !this.isActive()) return
        if (this._exitConfirmOpen || isDemoExitConfirmActive()) return
        if (this._restartConfirmOpen) return
        if (tourManager.isActive()) return

        if (this.isGuideDone() || !this.hasResumableDemoTour()) {
            this.markGuideDone()
            this.requestRestart()
            return
        }

        this.resume()
    }

    requestRestart() {
        if (!this.isActive()) return
        if (this._restartConfirmOpen || this._exitConfirmOpen || isDemoExitConfirmActive()) return

        this._restartConfirmOpen = true

        document.dispatchEvent(
            new CustomEvent('showAviso', {
                detail: {
                    title: 'Refazer a demonstração',
                    message:
                        'O guia da demonstração já foi concluído. Deseja recomeçar do início?<br><br>' +
                        'A página será recarregada e os dados fictícios atuais serão substituídos por uma nova sessão.',
                    callbackLabel: 'Refazer demonstração',
                    callbackLabelClose: 'Continuar no modo livre',
                    callback: () => {
                        this._restartConfirmOpen = false
                        this.restartFromScratch()
                    },
                    callbackClose: () => {
                        this._restartConfirmOpen = false
                    },
                },
            })
        )
    }

    restartFromScratch() {
        try {
            sessionStorage.setItem(SESSION_KEY, '1')
            sessionStorage.setItem(RESTART_KEY, '1')
            sessionStorage.removeItem(GUIDE_DONE_KEY)
            sessionStorage.removeItem(DEMO_PAUSE_KEY)
            sessionStorage.removeItem(PROGRESS_STORAGE_KEY)
            sessionStorage.removeItem(RESUME_STORAGE_KEY)
            sessionStorage.removeItem(STEP_CONTEXT_STORAGE_KEY)
            sessionStorage.removeItem(STORE_KEY)
        } catch (error) {}

        if (tourManager.isActive()) {
            tourManager.destroy()
        }

        const atHome =
            window.location.pathname === '/' &&
            !window.location.search &&
            !window.location.hash

        if (atHome) {
            window.location.reload()
            return
        }

        window.location.assign('/')
    }

    startPendingRestart() {
        if (!this._pendingRestart || !this.isActive()) return
        this._pendingRestart = false

        setTimeout(() => {
            this.start({ startTour: 'demo-equipamento', resumeIndex: 0 })
        }, 400)
    }

    async resume() {
        if (!canUseTours() || !this.isActive()) return
        if (this._exitConfirmOpen || isDemoExitConfirmActive()) return
        if (this._restartConfirmOpen) return

        if (this.isGuideDone()) {
            this.requestRestart()
            return
        }

        const pause = this.getPausedTour()
        let tourName = pause?.tourName
        let resumeIndex = pause?.resumeIndex ?? 0
        const pausePath = pause?.path
        const activeTabs = Array.isArray(pause?.activeTabs) ? pause.activeTabs : []

        if (!tourName) {
            try {
                const rawProgress = sessionStorage.getItem(PROGRESS_STORAGE_KEY)
                const progress = rawProgress ? JSON.parse(rawProgress) : null
                if (progress?.tourName?.startsWith?.('demo-')) {
                    tourName = progress.tourName
                    resumeIndex = progress.resumeIndex ?? 0
                }
            } catch (error) {}
        }

        if (!tourName) {
            this.markGuideDone()
            this.requestRestart()
            return
        }

        if (tourManager.isActive()) {
            tourManager.destroy()
        }

        const currentPath =
            window.location.pathname + window.location.search + window.location.hash

        if (pausePath && pausePath !== currentPath) {
            if (tourManager.history) {
                tourManager.history.push(pausePath)
            } else {
                window.location.href = pausePath
                return
            }
        }

        if (activeTabs.length > 0) {
            const firstTabSelector = `[data-tour="${activeTabs[0]}"]`

            try {
                await tourManager.waitForElement(firstTabSelector)
            } catch (error) {}

            activeTabs.forEach((tourId) => {
                const tab = document.querySelector(`[data-tour="${tourId}"]`)
                if (tab && !tab.classList.contains('react-tabs__tab--selected')) {
                    tab.click()
                }
            })
        }

        this.clearPause()
        setDemoResumeVisible(false)
        await tourManager.start(tourName, { resumeIndex })
    }

    start(options = {}) {
        if (!canUseTours()) {
            return
        }

        this.active = true

        try {
            sessionStorage.setItem(SESSION_KEY, '1')
            sessionStorage.removeItem(STEP_CONTEXT_STORAGE_KEY)
            sessionStorage.removeItem(GUIDE_DONE_KEY)
            sessionStorage.removeItem(RESTART_KEY)
        } catch (error) {}

        demoStore.clear()
        demoStore.seed()
        enableDemoInterceptor()
        this.hookMinimize()
        this.mountUi()
        this.attachRouteGuard()
        this.attachFieldInteractionGuard()
        this.attachSaveButtonGuard()
        this.attachHelpGuard()
        this.clearPause()

        const startTour = options.startTour || 'demo-equipamento'
        const resumeIndex = options.resumeIndex ?? 0

        if (tourManager.isActive()) {
            tourManager.destroy()
        }

        tourManager.start(startTour, { resumeIndex })
    }

    stop({ redirectTo = '/' } = {}) {
        this.active = false
        this._exitConfirmOpen = false
        this._restartConfirmOpen = false
        setDemoExitConfirmActive(false)
        this.detachRouteGuard()
        this.detachFieldInteractionGuard()
        this.detachSaveButtonGuard()
        this.detachHelpGuard()
        this.clearPause()

        try {
            sessionStorage.removeItem(SESSION_KEY)
            sessionStorage.removeItem(RESUME_STORAGE_KEY)
            sessionStorage.removeItem(PROGRESS_STORAGE_KEY)
            sessionStorage.removeItem(DEMO_PAUSE_KEY)
            sessionStorage.removeItem(STEP_CONTEXT_STORAGE_KEY)
            sessionStorage.removeItem(STORE_KEY)
            sessionStorage.removeItem(GUIDE_DONE_KEY)
            sessionStorage.removeItem(RESTART_KEY)
        } catch (error) {}

        if (tourManager.isActive()) {
            tourManager.destroy()
        }

        disableDemoInterceptor()
        demoStore.clear()
        unmountDemoExitUi()
        if (this._onDemoExitRequest) {
            document.removeEventListener(
                'hdv-demo-request-exit',
                this._onDemoExitRequest
            )
            this._onDemoExitRequest = null
        }
        this.guardOpen = false
        this.bootstrapped = false

        if (redirectTo === false) return

        const target = redirectTo || '/'
        window.location.assign(target)
    }

    attachRouteGuard() {
        this.detachRouteGuard()
        this.lastAllowedPath = window.location.pathname || '/'

        const check = (pathname) => {
            if (!this.isActive()) return
            if (isDemoAllowedPath(pathname)) {
                this.lastAllowedPath = pathname
                return
            }
            this.warnOutsideDemo(pathname)
        }

        if (tourManager.history?.listen) {
            this.routeUnlisten = tourManager.history.listen((location) => {
                check(location.pathname)
            })
        }

        check(window.location.pathname)

        this._onDocumentClick = (event) => {
            if (!this.isActive()) return
            const link = event.target.closest?.('a[href]')
            if (!link) return

            const href = link.getAttribute('href')
            if (!href || href.startsWith('#') || href.startsWith('http')) return

            try {
                const url = new URL(href, window.location.origin)
                if (!isDemoAllowedPath(url.pathname)) {
                    event.preventDefault()
                    event.stopPropagation()
                    this.warnOutsideDemo(url.pathname)
                }
            } catch (error) {}
        }
        document.addEventListener('click', this._onDocumentClick, true)
    }

    detachRouteGuard() {
        if (typeof this.routeUnlisten === 'function') {
            this.routeUnlisten()
            this.routeUnlisten = null
        }
        if (this._onDocumentClick) {
            document.removeEventListener('click', this._onDocumentClick, true)
            this._onDocumentClick = null
        }
    }

    warnOutsideDemo(pathname) {
        if (this.guardOpen) return
        this.guardOpen = true

        const goBack = () => {
            this.guardOpen = false
            const fallback = isDemoAllowedPath(this.lastAllowedPath)
                ? this.lastAllowedPath
                : '/'
            if (tourManager.history) {
                tourManager.history.push(fallback)
            } else {
                window.location.href = fallback
            }
        }

        helper.dispatchEvent('showAviso', {
            title: 'Fora do modo demonstração',
            message:
                `A página <strong>${pathname}</strong> não faz parte da demonstração guiada.<br><br>` +
                'Para utilizar essa área do sistema, saia do modo demonstração. Os dados fictícios serão descartados.',
            callbackLabel: 'Sair do modo demonstração',
            callback: () => {
                this.guardOpen = false
                this.stop({ redirectTo: pathname })
            },
            callbackLabelClose: 'Voltar à demonstração',
            callbackClose: goBack,
        })
    }
}

const demoSession = new DemoSession()

export default demoSession
